import { useEffect, useMemo } from "react";
import { Select } from "antd";
import { useIsAdmin } from "../../hooks/useEffectiveRole";
import { useOrgUsers } from "../../context/OrgUsersContext";

// Same portal-flavor normalisation as UserAssignmentSelect. Callers
// pass any of these flavours and we resolve to the user-row field
// name returned by GET /access/users. The field value is EFFECTIVE
// access (user-flag AND org-flag) so an org-disabled portal hides
// teammates server-side regardless of their personal flag.
const PORTAL_TO_FLAG = {
  "income-tax": "enable_income_tax_portal",
  income_tax: "enable_income_tax_portal",
  "gstin": "enable_gst_portal",
  gst: "enable_gst_portal",
  GST: "enable_gst_portal",
  tds: "enable_tds_portal",
  TDS: "enable_tds_portal",
  "income-Tax": "enable_income_tax_portal",
};

// Visibility-lens user filter for list pages (USER_LEVEL_CLIENT_ASSIGNMENT.md
// §7.2). Distinct from <UserAssignmentSelect /> — that component PICKS an
// assignment (mutates state). This component FILTERS the visible list
// (read-only lens; backend already auto-scopes regular users).
//
// Render rules per §7.2:
//
//   | Role             | Default        | Options shown                                  |
//   | ---------------- | -------------- | ---------------------------------------------- |
//   | Single-user org  | render NOTHING (handled by hideWhenSingleUser default) |
//   | Regular user     | render NOTHING (only "Mine + Shared" available, which is the BE default — no filter to offer) |
//   | Admin            | "All users"    | "All users", "Unassigned", per-user options    |
//
// Submit-side mapping (call site forwards to the list endpoint as
// `?assigned_user_id=<value>`):
//   ALL         → omit the param entirely (BE applies default visibility scope)
//   UNASSIGNED  → pass the literal string "unassigned"
//   <uuid>      → pass the user's uuid
//
// Use `toQueryParam(value)` to convert the dropdown's internal value to
// the BE param. Returns `undefined` for ALL so callers can spread the
// result into URLSearchParams without polluting the query.

export const ALL_USERS = "__all__";
export const UNASSIGNED = "unassigned";

// Convert the dropdown's selected value to the value that should be
// forwarded as ?assigned_user_id=<v>. Returns undefined for ALL_USERS
// so callers can drop the param entirely.
export const toQueryParam = (value) => {
  if (value === undefined || value === null || value === ALL_USERS) {
    return undefined;
  }
  return value;
};

const UserFilterDropdown = ({
  value,
  onChange,
  // When true (default), render nothing for single-user orgs and for
  // regular users (per §7.2). Callers don't usually need to override.
  hideWhenSingleUser = true,
  hideForRegularUsers = true,
  size = "middle",
  // Width override; default is a sensible 200px so it sits next to other
  // filter controls without dominating the row.
  style,
  // Portal scope — when provided, only users who have effective access
  // to that portal appear in the dropdown. Mirrors UserAssignmentSelect's
  // portal prop. Lets per-portal list pages (IT/GST/TDS) hide team
  // members who don't have access to the page's portal — avoids the
  // "filter by Bharat → 0 rows" UX when Bharat isn't on this portal.
  // null/undefined → no portal filter (legacy multi-portal pages).
  portal = null,
}) => {
  // Role via the shared hook — correctly prefers the embed JWT inside the
  // /embed/* iframe (a stale localStorage admin in the same browser must
  // not promote a non-admin embed user, and vice-versa).
  const isAdmin = useIsAdmin();
  const { users, loaded, isSingleUserOrg, ensureLoaded } = useOrgUsers();

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  // Resolve any portal-name flavor → flag field. null → no filter.
  const portalFlagField = portal ? PORTAL_TO_FLAG[portal] || null : null;

  const options = useMemo(() => {
    if (!isAdmin) return [];
    const filtered = portalFlagField
      ? users.filter((u) => u[portalFlagField] !== false)
      : users;
    return [
      { value: ALL_USERS, label: "All users" },
      { value: UNASSIGNED, label: "Unassigned" },
      ...filtered.map((u) => ({
        value: u.id,
        label: u.full_name ? `${u.full_name} (${u.email})` : u.email,
      })),
    ];
  }, [users, isAdmin, portalFlagField]);

  if (!loaded) return null;
  if (hideWhenSingleUser && isSingleUserOrg) return null;
  if (hideForRegularUsers && !isAdmin) return null;

  return (
    <Select
      value={value ?? ALL_USERS}
      onChange={onChange}
      options={options}
      size={size}
      style={{ minWidth: 200, ...style }}
      allowClear={false}
      placeholder="Filter by user"
    />
  );
};

export default UserFilterDropdown;
